import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { differenceInDays } from 'date-fns/differenceInDays';
import { useMemo, useState } from 'react';

import { EmptyState } from '../../../components/empty-state';
import { Loading } from '../../../components/loading';
import { Tooltip } from '../../../components/popover/tooltip';
import { Cell, Row, Table } from '../../../components/tables';
import type { OnboardingOverviewStore } from '../../../data/onboarding';
import type { MilestoneFlags } from '../../../data/store';
import { compare } from '../../../helpers/column-sort';
import { formatDate } from '../../../helpers/format-date';
import { useOnboardingOverview } from '../../../hooks/onboarding';

type OnboardingSort = 'stalled' | 'newest' | 'trial' | 'name';

function daysSince(date: Date | undefined): number | undefined {
  return date ? differenceInDays(new Date(), date) : undefined;
}

function trialDaysRemaining(trialEnd: Date | undefined | null): number | undefined {
  return trialEnd ? differenceInCalendarDays(trialEnd, new Date()) : undefined;
}

function compareRows(
  a: OnboardingOverviewStore,
  b: OnboardingOverviewStore,
  sort: OnboardingSort
): number {
  switch (sort) {
    case 'newest':
      return compare(b.store.created_at, a.store.created_at);

    case 'trial':
      return compare(trialDaysRemaining(a.store.trial_end), trialDaysRemaining(b.store.trial_end));

    case 'name':
      return compare(a.store.name, b.store.name);

    case 'stalled':
      // Stores with no activity rank as most stalled (treat as +Infinity days).
      return compare(
        daysSince(b.last_event_at) ?? Number.POSITIVE_INFINITY,
        daysSince(a.last_event_at) ?? Number.POSITIVE_INFINITY
      );
  }
}

const SORT_OPTIONS: readonly { value: OnboardingSort; label: string }[] = [
  { value: 'stalled', label: 'Most stalled' },
  { value: 'newest', label: 'Newest signups' },
  { value: 'trial', label: 'Trial ending soon' },
  { value: 'name', label: 'Name (A→Z)' },
];

// Visual ordering of the five milestone dots — operational progression first,
// then the "real revenue" capstone (vendor → upload → order → receiving → conversion).
const MILESTONE_ORDER: readonly { key: keyof MilestoneFlags; letter: string; label: string }[] = [
  { key: 'vendor_integrated', letter: 'V', label: 'Vendor Integration' },
  { key: 'uploaded_data', letter: 'U', label: 'Store Data Uploaded' },
  { key: 'sent_first_order', letter: 'O', label: 'First Order Sent' },
  { key: 'received_first_order', letter: 'R', label: 'First Invoice Received' },
  { key: 'trial_converted', letter: '$', label: 'Converted to Paid Customer (trial ended)' },
];

function MilestoneChecklist({ flags }: { flags: MilestoneFlags }) {
  return (
    <ul className="flex list-none items-center gap-1 p-0" aria-label="Milestones">
      {MILESTONE_ORDER.map(({ key, letter, label }) => {
        const reached = flags[key];

        return (
          <li key={key}>
            <Tooltip content={label} side="bottom">
              <span
                role="img"
                aria-label={label}
                className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold ${
                  reached ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {letter}
              </span>
            </Tooltip>
          </li>
        );
      })}
    </ul>
  );
}

type OnboardingStatus =
  | 'Trial expired'
  | 'No activity yet'
  | 'Active'
  | 'Stalling'
  | 'Stalled'
  | 'Unknown';

interface StatusInputs {
  daysSinceActivity: number | undefined;
  trialDays: number | undefined;
  hasAnyEvent: boolean;
}

function classifyStatus({
  daysSinceActivity,
  trialDays,
  hasAnyEvent,
}: StatusInputs): OnboardingStatus {
  if (trialDays !== undefined && trialDays < 0) {
    return 'Trial expired';
  }

  if (!hasAnyEvent) {
    return 'No activity yet';
  }

  if (daysSinceActivity === undefined) {
    return 'Unknown';
  }

  if (daysSinceActivity < 7) {
    return 'Active';
  }

  if (daysSinceActivity < 14) {
    return 'Stalling';
  }

  return 'Stalled';
}

const STATUS_CLASS: Record<OnboardingStatus, string> = {
  'Trial expired': 'bg-red-100 text-red-800',
  'No activity yet': 'bg-gray-100 text-gray-800',
  Active: 'bg-green-100 text-green-800',
  Stalling: 'bg-yellow-100 text-yellow-800',
  Stalled: 'bg-red-100 text-red-800',
  Unknown: 'bg-gray-100 text-gray-800',
};

interface PillProps {
  lastEventAt: Date | undefined;
  trialDays: number | undefined;
  hasAnyEvent: boolean;
}

function StatusPill({ lastEventAt, trialDays, hasAnyEvent }: PillProps) {
  const daysSinceActivity = daysSince(lastEventAt);
  const status = classifyStatus({ daysSinceActivity, trialDays, hasAnyEvent });
  const className = STATUS_CLASS[status];

  let label: string = status;
  if ((status === 'Active' || status === 'Stalling' || status === 'Stalled') && lastEventAt) {
    label = `${status} (${formatDate(lastEventAt, new Date()).trim()} ago)`;
  }

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
}

const TABLE_HEADINGS = [
  'Store',
  'Owner',
  'Email',
  'Customer since',
  'Milestones',
  'Trial days left',
  'Status',
];

export function OnboardingOverviewTab() {
  const navigate = useNavigate();
  const { data, isLoading } = useOnboardingOverview();
  const [sort, setSort] = useState<OnboardingSort>('stalled');

  const rows = useMemo(
    () => [...(data?.stores ?? [])].sort((a, b) => compareRows(a, b, sort)),
    [data, sort]
  );

  if (!data && isLoading) {
    return <Loading />;
  }

  if ((data?.stores.length ?? 0) === 0) {
    return (
      <div className="container mx-auto p-4">
        <EmptyState icon={<BuildingStorefrontIcon className="size-8" />} title="No stores yet." />
      </div>
    );
  }

  return (
    <div className="container mx-auto space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Onboarding Overview</h1>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Sort by</span>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSort(opt.value)}
              aria-pressed={sort === opt.value}
              className={`rounded-md px-2 py-1 ${
                sort === opt.value ? 'bg-gray-200 font-medium' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <Table headings={TABLE_HEADINGS}>
        {rows.map((row) => {
          const trialDays = trialDaysRemaining(row.store.trial_end);

          return (
            <Row
              key={row.store.id}
              className="cursor-pointer hover:bg-gray-100"
              onClick={() =>
                navigate({
                  to: '/admin/onboarding/$storeId',
                  params: { storeId: row.store.id },
                })
              }
            >
              <Cell>{row.store.name}</Cell>
              <Cell>{`${row.store.owner_first_name} ${row.store.owner_last_name}`.trim()}</Cell>
              <Cell>{row.owner_email}</Cell>
              <Cell>{formatDate(row.store.created_at, new Date()).trim()}</Cell>
              <Cell>
                <MilestoneChecklist flags={row.store.milestones} />
              </Cell>
              <Cell>{trialDays === undefined ? '—' : String(trialDays)}</Cell>
              <Cell>
                <StatusPill
                  lastEventAt={row.last_event_at}
                  trialDays={trialDays}
                  hasAnyEvent={!!row.last_event_at}
                />
              </Cell>
            </Row>
          );
        })}
      </Table>
    </div>
  );
}
